type PartitionCookieDetectionReason =
  | 'ssr'
  | 'top-level'
  | 'non-https'
  | 'cookies-disabled'
  | 'probe-success'
  | 'probe-failed';

type PartitionCookieDetectionResult = {
  inIframe: boolean;
  cookiesUsable: boolean;
  reason: PartitionCookieDetectionReason;
};

const PARTITION_COOKIE_ATTRS = 'Path=/; Secure; SameSite=None; Partitioned';

const buildResult = (
  inIframe: boolean,
  cookiesUsable: boolean,
  reason: PartitionCookieDetectionReason
): PartitionCookieDetectionResult => ({ inIframe, cookiesUsable, reason });

/**
 * Cheap iframe check — no probe cookie, safe during SSR. A cross-origin frame throws on
 * `window.top` access, which itself means we're framed, so that case returns `true`.
 */
export const isInIframe = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/**
 * Detects whether partitioned (CHIPS) cookies are usable in the current context.
 * =============================================================================
 *
 * Runs cheap context checks first (SSR, top-level, non-HTTPS, cookies disabled)
 * and only writes a short-lived probe cookie when the document is actually
 * embedded over HTTPS. The probe is always cleaned up before returning, and a
 * throwing read-back is treated as `probe-failed` rather than propagating.
 *
 * Safe to call during SSR; returns `{ reason: 'ssr' }` when invoked without a DOM.
 *
 * Usage example:
 *   import { detectPartitionCookieSupport } from 'js/lib/partitionCookieDetect';
 *   const { cookiesUsable, reason } = detectPartitionCookieSupport();
 *   if (!cookiesUsable) logger.warn('CHIPS unavailable', { reason });
 */
export const detectPartitionCookieSupport = (): PartitionCookieDetectionResult => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return buildResult(false, false, 'ssr');
  }

  const inIframe = isInIframe();
  const protocol = window.location.protocol;

  if (!inIframe) {
    return buildResult(false, true, 'top-level');
  }

  if (protocol !== 'https:') {
    return buildResult(true, false, 'non-https');
  }

  if (typeof navigator !== 'undefined' && navigator.cookieEnabled === false) {
    return buildResult(true, false, 'cookies-disabled');
  }

  const name = `__partition_probe_${Math.random().toString(36).slice(2)}`;

  let probeReadBack = false;
  try {
    document.cookie = `${name}=1; ${PARTITION_COOKIE_ATTRS}`;
    probeReadBack = document.cookie.includes(`${name}=1`);
  } catch {
    probeReadBack = false;
  } finally {
    document.cookie = `${name}=; ${PARTITION_COOKIE_ATTRS}; Max-Age=0`;
  }

  return buildResult(true, probeReadBack, probeReadBack ? 'probe-success' : 'probe-failed');
};
